import { Link, createFileRoute } from '@tanstack/react-router';
import { Zap } from 'lucide-react';
import { useEffect } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { HABITS } from '@/lib/i18n/habits';
import { TrainingShortcuts } from '@/components/routine/TrainingShortcuts';
import { ActiveRoutineLink } from '@/components/routine/ActiveRoutineLink';
import { ActiveSessionCard } from '@/components/workout/ActiveSessionCard';
import { RecentSessions } from '@/components/workout/RecentSessions';
import { RoutineStartList } from '@/components/workout/RoutineStartList';
import { SyncBadge } from '@/components/workout/SyncBadge';
import { useAuth } from '@/lib/auth';
import { pruneSyncedSessions } from '@/lib/offline/session';
import { useActiveSession, useLocalSets } from '@/lib/queries/workouts';

export const Route = createFileRoute('/_app/train/')({
  component: TrainPage,
});

/** Saludo según la hora local; detalle pequeño que hace la app menos genérica. */
function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 6) return 'Buenas noches';
  if (hour < 13) return 'Buenos días';
  if (hour < 21) return 'Buenas tardes';
  return 'Buenas noches';
}

/** Series de la sesión en curso; `''` cuando no hay ninguna abierta. */
function useActiveSetsCount(clientId: string): number {
  const sets = useLocalSets(clientId);
  return sets?.length ?? 0;
}

function TrainPage() {
  const { user } = useAuth();
  const active = useActiveSession();
  const setsCount = useActiveSetsCount(active?.clientId ?? '');

  // La base local no es un archivo: lo viejo y ya sincronizado lo sirve el
  // servidor, así que se limpia al entrar en la pestaña.
  useEffect(() => {
    void pruneSyncedSessions();
  }, []);

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${user?.displayName ?? ''}`}
        subtitle="Registra tus series, aunque el gimnasio no tenga cobertura."
      />

      <ActiveRoutineLink />

      {/* Escritorio: arriba la acción principal (con la sincronización al
          lado); debajo, dos columnas que apilan cada una lo suyo, las rutinas
          a la izquierda y accesos e historial a la derecha. Cada columna es un
          solo elemento de la rejilla: si fueran sueltos, las filas se
          estirarían a la altura de la columna más alta y dejarían huecos en
          la otra. En móvil las columnas no existen (`contents`) y queda el
          orden de siempre. */}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-start lg:gap-5">
        <SyncBadge className="lg:col-start-2 lg:row-start-1 lg:self-center" />

        <div className="lg:col-start-1 lg:row-start-1">
          {active === null || active === undefined ? (
            // Secundario: lo normal es empezar un día de la rutina (la lista de
            // abajo); a quien empieza, un botón verde de «libre» arriba del todo
            // le distraía del primer paso, que es copiar una plantilla.
            <Link
              to="/train/session/new"
              search={{}}
              className="flex min-h-13 items-center justify-center gap-2 rounded-xl border border-line bg-surface-raised px-5 text-base font-semibold text-ink transition-colors hover:bg-surface-overlay active:bg-surface-overlay"
            >
              <Zap className="size-5 text-brand" aria-hidden="true" />
              Empezar entrenamiento libre
            </Link>
          ) : (
            <ActiveSessionCard session={active} setsCount={setsCount} />
          )}
        </div>

        <div className="contents lg:col-start-1 lg:flex lg:flex-col lg:gap-5">
          <RoutineStartList />
        </div>
        <div className="contents lg:col-start-2 lg:flex lg:flex-col lg:gap-5">
          <TrainingShortcuts />
          <Card title={HABITS.today} description={HABITS.shortcut}>
            <Link
              to="/progress"
              className="flex min-h-11 items-center text-sm font-semibold text-brand"
            >
              {HABITS.open}
            </Link>
          </Card>
          <RecentSessions />
        </div>
      </div>
    </>
  );
}
